// Live-tracking user marker: Ozzy snout icon + a translucent blue directional
// cone that rotates with the device compass heading. Uses an imperative
// Leaflet marker so we can mutate the cone's rotation on every heading tick
// (up to ~60Hz) without React re-rendering the map layer tree.
//
// Extracted verbatim from MapPage.jsx so trail-detail mini-maps and the
// full map can share one implementation. The CSS (.td-live-user*) and
// the /ozzy-snout.png asset are shared globally — no duplication.
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

export default function LiveUserMarker({ userPos, heading }) {
  const map = useMap();
  const markerRef = useRef(null);
  const iconRef = useRef(null);

  if (!iconRef.current) {
    iconRef.current = L.divIcon({
      className: "",
      // 140×140 gives the cone room to fan out; snout sits centred on the
      // user's lat/lng so pin accuracy is preserved across zoom levels.
      iconSize: [140, 140],
      iconAnchor: [70, 70],
      html:
        `<div class="td-live-user">` +
        `<div class="td-live-user-cone" data-testid="user-heading-cone"></div>` +
        `<img class="td-live-user-snout" src="/ozzy-snout.png" alt="You are here" draggable="false" />` +
        `</div>`,
    });
  }

  // Position lifecycle — create / move / remove the Leaflet marker.
  useEffect(() => {
    if (!userPos) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }
    if (!markerRef.current) {
      markerRef.current = L.marker(userPos.position, {
        icon: iconRef.current,
        interactive: false,
        keyboard: false,
        zIndexOffset: 500,
      }).addTo(map);
    } else {
      markerRef.current.setLatLng(userPos.position);
    }
  }, [userPos, map]);

  // Heading updates: mutate the cone's transform directly to avoid React
  // reconciling the DOM on every deviceorientation tick.
  useEffect(() => {
    const el = markerRef.current && markerRef.current.getElement();
    if (!el) return;
    const cone = el.querySelector(".td-live-user-cone");
    if (!cone) return;
    if (typeof heading === "number" && !Number.isNaN(heading)) {
      cone.style.opacity = "1";
      cone.style.transform = `translate(-50%, -100%) rotate(${heading}deg)`;
    } else {
      cone.style.opacity = "0";
    }
  }, [heading, userPos]);

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, []);

  return null;
}
